import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import { LEAD_PRODUCTS } from "../services/leadsService";
import { runAttachmentsImport } from "../services/attachmentsService";
import { isAxiosClientTimeout } from "../../../utils/api";
import { buildThemeClasses } from "../theme";
import {
  fetchHubspotStatus,
  fetchHubspotOauthUrl,
  fetchHubspotPipelines,
  applyHubspotProductMapping,
  fetchHubspotRuns,
  fetchHubspotEvents,
  testHubspotConnection,
  disconnectHubspot,
  saveHubspotCredentials,
  forgetHubspotCredentials,
  updateHubspotConfig,
  runHubspotSync,
  runHubspotFullImport,
  HUBSPOT_OWNERS_KEY,
  runHubspotMarketingSync,
  syncHubspotOwners,
  scopeGuideOf,
  scopesToCopy,
  type HubspotStatus,
  type HubspotObjectStatus,
  type HubspotPipeline,
  type HubspotRun,
  type HubspotEvent,
  type HubspotCredentialsInput,
  type SyncDirection,
} from "../services/hubspotService";

// ===========================================================================
// Configuración → HubSpot — the control surface for the two-way suite sync.
//
// This screen is the WHOLE setup: the tenant pastes its own HubSpot keys here
// and the portal is connected. Nothing is deployed, and the platform ships no
// HubSpot environment variable — every tenant brings its own account.
//
// The sync itself never runs from here: Firestore triggers push our edits, the
// HubSpot webhook pushes theirs, and a scheduled job reconciles both. This
// screen decides WHAT syncs and in WHICH direction, lines our pipeline stages up
// with theirs, forces a run, and shows the history when something looks off.
//
// Four tabs so the page stays legible:
//   Conexión  — paste the keys, then the setup values to copy into HubSpot
//               (webhook URL, redirect URI, subscriptions, scopes)
//   Objetos   — per-object toggle + direction + linked count + "sync just this"
//   Mapeo     — which HubSpot pipeline/stage each of our stages corresponds to
//   Historial — sync runs and the inbound webhook event log
// ===========================================================================

const DIRECTION_LABELS: Record<SyncDirection, string> = {
  two_way: "Bidireccional",
  pull: "Solo importar",
  push: "Solo enviar",
  off: "Desactivado",
};

// Our pipeline stages, in board order — the left-hand side of the stage map.
const LOCAL_DEAL_STAGES = [
  "nuevo",
  "contactado",
  "agendado",
  "calificado",
  "propuesta",
  "negociacion",
  "ganado",
  "perdido",
];

const LOCAL_TICKET_STATUSES = ["nuevo", "en_proceso", "esperando", "resuelto"];

type Tab = "conexion" | "objetos" | "mapeo" | "historial";

function fmtDate(value?: string | number | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("es-MX");
}

function errText(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message || err?.message || fallback;
}

export default function HubspotSettings() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses();

  const [tab, setTab] = useState<Tab>("conexion");
  const [status, setStatus] = useState<HubspotStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** «Empresas (1 de 12)» while a full import walks the objects. */
  const [importStep, setImportStep] = useState<string | null>(null);

  const [pipelines, setPipelines] = useState<HubspotPipeline[]>([]);
  const [ticketPipelines, setTicketPipelines] = useState<HubspotPipeline[]>([]);
  const [runs, setRuns] = useState<HubspotRun[]>([]);
  const [events, setEvents] = useState<HubspotEvent[]>([]);

  // Draft copies so the maps stay editable without a round-trip per keystroke.
  const [dealStageDraft, setDealStageDraft] = useState<Record<string, string>>({});
  const [ticketStageDraft, setTicketStageDraft] = useState<Record<string, string>>({});
  const [dealPipelineDraft, setDealPipelineDraft] = useState("default");
  const [ticketPipelineDraft, setTicketPipelineDraft] = useState("0");

  // The credentials form. Secrets start EMPTY on purpose: the server never
  // returns them, and leaving a field blank means "keep what is stored".
  const [keys, setKeys] = useState<HubspotCredentialsInput>({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [apiBaseDraft, setApiBaseDraft] = useState("");
  const [webhookDraft, setWebhookDraft] = useState("");
  const [redirectDraft, setRedirectDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await fetchHubspotStatus();
      setStatus(s);
      setDealStageDraft(s.maps.dealStage || {});
      setTicketStageDraft(s.maps.ticketStage || {});
      setDealPipelineDraft(s.dealPipelineId || "default");
      setTicketPipelineDraft(s.ticketPipelineId || "0");
      // Only the public identifiers are pre-filled; secrets stay blank.
      setKeys({
        clientId: s.credentials.clientId || "",
        appId: s.credentials.appId || "",
        portalId: s.credentials.portalId || "",
      });
      setApiBaseDraft(s.settings.publicApiBase || "");
      setWebhookDraft(s.settings.webhookUrl || "");
      setRedirectDraft(s.settings.redirectUri || "");
    } catch (e) {
      setError(errText(e, "No se pudo cargar el estado de HubSpot"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The OAuth callback sends the browser back here with ?connected=1|0. Report
  // the outcome and clean the URL so a refresh doesn't repeat the message.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    if (connected === null) return;

    const detail = params.get("detail");
    Swal.fire({
      icon: connected === "1" ? "success" : "error",
      title: connected === "1" ? "HubSpot conectado" : "No se pudo conectar",
      text:
        connected === "1"
          ? `Autorización completada${detail ? ` para ${detail}` : ""}.`
          : detail || "HubSpot rechazó la autorización.",
      confirmButtonColor: brand.primary,
    });

    params.delete("connected");
    params.delete("detail");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    load();
  }, [load]);

  // The heavier reads only load when their tab is actually opened.
  useEffect(() => {
    if ((tab !== "mapeo" && tab !== "objetos") || !status?.connected || pipelines.length) return;
    (async () => {
      try {
        setPipelines(await fetchHubspotPipelines("deals"));
        setTicketPipelines(await fetchHubspotPipelines("tickets"));
      } catch (e) {
        setError(errText(e, "No se pudieron cargar los pipelines de HubSpot"));
      }
    })();
  }, [tab, status?.connected, pipelines.length]);

  useEffect(() => {
    if (tab !== "historial") return;
    (async () => {
      try {
        const [r, ev] = await Promise.all([fetchHubspotRuns(20), fetchHubspotEvents(50)]);
        setRuns(r);
        setEvents(ev);
      } catch (e) {
        setError(errText(e, "No se pudo cargar el historial"));
      }
    })();
  }, [tab]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      Swal.fire({ icon: "success", title: "Copiado", timer: 1100, showConfirmButton: false });
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  /** Wrap an action with the busy flag, an error toast and a status refresh. */
  const act = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      // The browser gave up waiting, the server did not: a sync keeps running
      // (and usually finishes) after the client times out. Say so instead of
      // «Error», or people press the button again and stack a second run.
      if (isAxiosClientTimeout(e)) {
        Swal.fire({
          icon: "info",
          title: "Sigue trabajando en el servidor",
          text:
            "La operación tarda más de lo que esta pantalla espera, pero no se detuvo. " +
            "No la lances otra vez: revisa la pestaña Historial en unos minutos para ver el resultado.",
          confirmButtonColor: brand.primary,
        });
        await load().catch(() => undefined);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errText(e, "La operación falló"),
          confirmButtonColor: brand.primary,
        });
      }
    } finally {
      setBusy(null);
    }
  };

  // --- Credentials ----------------------------------------------------------

  const handleSaveKeys = () =>
    act("keys", async () => {
      // Send only what the user actually filled in, so blank secret fields keep
      // whatever is already stored instead of wiping it.
      const payload: HubspotCredentialsInput = {};
      for (const field of ["accessToken", "clientId", "clientSecret", "appId", "portalId"] as const) {
        const value = keys[field];
        if (value !== undefined && value !== null) payload[field] = value;
      }
      if (Object.keys(payload).length === 0) {
        Swal.fire({
          icon: "info",
          title: "Nada que guardar",
          text: "Escribe al menos una llave.",
          confirmButtonColor: brand.primary,
        });
        return;
      }

      const r = await saveHubspotCredentials(payload);
      Swal.fire({
        icon: r.verified ? "success" : "warning",
        title: r.verified ? "HubSpot conectado" : "Llaves guardadas",
        text: r.verified
          ? "Las llaves funcionan: ya puedes sincronizar."
          : r.verifyError
            ? `Guardadas, pero HubSpot respondió: ${r.verifyError}`
            : "Guardadas. Completa el token de acceso o autoriza por OAuth para conectar.",
        confirmButtonColor: brand.primary,
      });
      setKeys((k) => ({ ...k, accessToken: "", clientSecret: "" }));
      await load();
    });

  const handleForgetKeys = async () => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Borrar las llaves de HubSpot",
      text: "La sincronización se detiene hasta que guardes llaves nuevas. Los registros ya sincronizados se quedan como están.",
      showCancelButton: true,
      confirmButtonText: "Borrar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#DC2626",
    });
    if (!res.isConfirmed) return;
    await act("forget", async () => {
      await forgetHubspotCredentials();
      setKeys({});
      await load();
    });
  };

  const handleSaveUrls = () =>
    act("urls", async () => {
      await updateHubspotConfig({
        settings: {
          publicApiBase: apiBaseDraft.trim() || null,
          webhookUrl: webhookDraft.trim() || null,
          redirectUri: redirectDraft.trim() || null,
        },
      });
      Swal.fire({
        icon: "success",
        title: "URLs guardadas",
        text: "Recuerda registrar la URL del webhook en HubSpot si cambió.",
        confirmButtonColor: brand.primary,
      });
      await load();
    });

  const changeDefaultProduct = (product: string) =>
    act("defaultProduct", async () => {
      await updateHubspotConfig({ settings: { defaultProduct: product || null } });
      await load();
    });

  const changePipelineProduct = (pipelineId: string, product: string) =>
    act("productByPipeline", async () => {
      const next = { ...(status?.settings.productByPipeline ?? {}) };
      if (product) next[pipelineId] = product;
      else delete next[pipelineId];
      await updateHubspotConfig({ settings: { productByPipeline: next } });
      await load();
    });

  const handleApplyProducts = () =>
    act("applyProducts", async () => {
      const r = await applyHubspotProductMapping();
      Swal.fire({
        icon: "success",
        title: "Productos aplicados",
        html: `<div style="text-align:left">
          <div><b>Negocios revisados:</b> ${r.deals}</div>
          <div><b>Negocios que cambiaron de producto:</b> ${r.dealsChanged}</div>
          <div><b>Contactos y empresas que cambiaron:</b> ${r.relatedChanged}</div>
        </div>`,
        confirmButtonColor: brand.primary,
      });
    });

  const togglePause = () =>
    act("pause", async () => {
      await updateHubspotConfig({ settings: { syncEnabled: !status?.settings.syncEnabled } });
      await load();
    });

  // --- Connection actions ---------------------------------------------------

  const handleConnect = () =>
    act("connect", async () => {
      const url = await fetchHubspotOauthUrl();
      window.open(url, "_blank", "noopener,noreferrer");
      Swal.fire({
        icon: "info",
        title: "Autoriza en HubSpot",
        text: "Termina el proceso en la pestaña nueva y vuelve aquí para recargar el estado.",
        confirmButtonColor: brand.primary,
      });
    });

  const handleTest = () =>
    act("test", async () => {
      const info = await testHubspotConnection();
      Swal.fire({
        icon: "success",
        title: "Conexión verificada",
        html: `<div style="text-align:left">
          <div><b>Portal:</b> ${info.portalId ?? "—"}</div>
          <div><b>Tipo de cuenta:</b> ${info.accountType ?? "—"}</div>
          <div><b>Moneda:</b> ${info.currency ?? "—"}</div>
          <div><b>Permisos:</b> ${info.scopes?.length ?? 0}</div>
        </div>`,
        confirmButtonColor: brand.primary,
      });
    });

  const handleDisconnect = async () => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Desconectar HubSpot",
      text: "Se olvidará la autorización OAuth. Los registros ya sincronizados se quedan como están.",
      showCancelButton: true,
      confirmButtonText: "Desconectar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#DC2626",
    });
    if (!res.isConfirmed) return;
    await act("disconnect", async () => {
      await disconnectHubspot();
      await load();
    });
  };

  // --- Sync actions ---------------------------------------------------------

  const runSync = (objects?: string[], full = false) =>
    act(objects?.length ? `sync:${objects[0]}` : full ? "sync:full" : "sync:all", async () => {
      const run = await runHubspotSync(objects, full);
      Swal.fire({
        icon: run.totals.errors ? "warning" : "success",
        title: full ? "Importación completa finalizada" : "Sincronización finalizada",
        html: `<div style="text-align:left">
          <div><b>Recibidos de HubSpot:</b> ${run.totals.pulled}</div>
          <div><b>Enviados a HubSpot:</b> ${run.totals.pushed}</div>
          <div><b>Creados:</b> ${run.totals.created} · <b>Actualizados:</b> ${run.totals.updated}</div>
          ${run.totals.errors ? `<div style="color:#DC2626"><b>Errores:</b> ${run.totals.errors} (ver Historial)</div>` : ""}
        </div>`,
        confirmButtonColor: brand.primary,
      });
      await load();
    });

  const handleFullImport = async () => {
    const res = await Swal.fire({
      icon: "question",
      title: "Importación completa",
      text: "Se leerá TODO el portal de HubSpot, no solo lo que cambió. Puede tardar varios minutos en cuentas grandes.",
      showCancelButton: true,
      confirmButtonText: "Importar todo",
      cancelButtonText: "Cancelar",
      confirmButtonColor: brand.primary,
    });
    if (!res.isConfirmed || !status) return;
    // One request per object: the whole portal in a single request ran into the
    // server's 300 s limit (≈1,500 records took 280 s in production).
    const enabled = status.objects.filter((o) => o.enabled);
    const labelOf = (key: string) => enabled.find((o) => o.key === key)?.label ?? key;
    await act("sync:full", async () => {
      try {
        const r = await runHubspotFullImport(
          enabled.map((o) => o.key),
          (key, i, total) => setImportStep(`${labelOf(key)} (${i + 1} de ${total})`),
        );
        // The browser gave up on one object: the server is still on it. `act`
        // explains that instead of reporting a failure.
        if (r.failed && isAxiosClientTimeout(r.failed.error)) throw r.failed.error;
        // Last step: the activities just imported say which files they carry;
        // copy them. Best-effort — it never turns a good import into a failure.
        let files: Awaited<ReturnType<typeof runAttachmentsImport>> | null = null;
        if (!r.failed) {
          setImportStep("Adjuntos");
          files = await runAttachmentsImport().catch(() => null);
        }
        const filesLine = !files
          ? ""
          : files.missingScope
            ? "<div><b>Adjuntos:</b> la clave de HubSpot no tiene los permisos <b>files</b> y <b>files.ui_hidden.read</b>; agrégalos y pulsa «Importar adjuntos».</div>"
            : files.found
              ? `<div><b>Adjuntos:</b> ${files.imported} copiados de ${files.found}${files.truncated ? " — quedan más: pulsa «Importar adjuntos» para continuar" : ""}</div>`
              : "";
        Swal.fire({
          icon: r.failed || r.totals.errors ? "warning" : "success",
          title: r.failed ? "Importación incompleta" : "Importación completa finalizada",
          html: `<div style="text-align:left">
            <div><b>Recibidos de HubSpot:</b> ${r.totals.pulled}</div>
            <div><b>Enviados a HubSpot:</b> ${r.totals.pushed}</div>
            <div><b>Creados:</b> ${r.totals.created} · <b>Actualizados:</b> ${r.totals.updated}</div>
            ${r.totals.errors ? `<div style="color:#DC2626"><b>Errores:</b> ${r.totals.errors} (ver Historial)</div>` : ""}
            ${filesLine}
            ${r.failed ? `<div style="color:#DC2626"><b>Se detuvo en:</b> ${labelOf(r.failed.object)} — ${errText(r.failed.error, "falló")}. Lo anterior sí quedó importado.</div>` : ""}
          </div>`,
          confirmButtonColor: brand.primary,
        });
        await load();
      } finally {
        setImportStep(null);
      }
    });
  };

  const handleMarketing = () =>
    act("marketing", async () => {
      await runHubspotMarketingSync();
      Swal.fire({
        icon: "success",
        title: "Marketing sincronizado",
        text: "Formularios, envíos (convertidos en leads), campañas y listas quedaron al día.",
        confirmButtonColor: brand.primary,
      });
      await load();
    });

  // Files attached to notes, e-mails, calls, meetings and tasks → documents of
  // each deal and contact. Time-boxed on the server: «truncated» = run it again.
  const handleAttachments = () =>
    act("attachments", async () => {
      const r = await runAttachmentsImport();
      Swal.fire({
        icon: r.missingScope || r.errors.length ? "warning" : "success",
        title: r.missingScope ? "Falta un permiso en HubSpot" : r.truncated ? "Importación de adjuntos en curso" : "Adjuntos importados",
        html: `<div style="text-align:left">
          ${
            r.missingScope
              ? "<div>La clave de servicio no puede leer archivos. En HubSpot, edita la clave y marca los permisos <b>files</b> y <b>files.ui_hidden.read</b>; guarda y vuelve a pulsar este botón. No hace falta cambiar nada aquí.</div>"
              : ""
          }
          <div><b>Archivos encontrados:</b> ${r.found}</div>
          <div><b>Copiados ahora:</b> ${r.imported} · <b>Ya estaban:</b> ${r.already + r.linked}</div>
          ${r.skipped ? `<div><b>Sin copiar (más de 25 MB):</b> ${r.skipped}</div>` : ""}
          ${r.truncated ? "<div><b>Quedan más:</b> vuelve a pulsar el botón para continuar.</div>" : ""}
          ${!r.missingScope && r.errors.length ? `<div style="color:#DC2626"><b>Con error:</b> ${r.errors.length}</div>` : ""}
        </div>`,
        confirmButtonColor: brand.primary,
      });
    });

  const handleOwners = () =>
    act("owners", async () => {
      const r = await syncHubspotOwners();
      Swal.fire({
        icon: "success",
        title: "Propietarios sincronizados",
        text: `${r.linked} propietarios vinculados con agentes (${r.createdAgents} agentes creados).`,
        confirmButtonColor: brand.primary,
      });
      await load();
    });

  // --- Config actions -------------------------------------------------------

  const toggleObject = (obj: HubspotObjectStatus) =>
    act(`toggle:${obj.key}`, async () => {
      await updateHubspotConfig({ enabled: { [obj.key]: !obj.enabled } });
      await load();
    });

  const changeDirection = (obj: HubspotObjectStatus, direction: SyncDirection) =>
    act(`dir:${obj.key}`, async () => {
      await updateHubspotConfig({ direction: { [obj.key]: direction } });
      await load();
    });

  const saveMaps = () =>
    act("maps", async () => {
      await updateHubspotConfig({
        dealPipelineId: dealPipelineDraft,
        ticketPipelineId: ticketPipelineDraft,
        maps: { dealStage: dealStageDraft, ticketStage: ticketStageDraft },
      });
      Swal.fire({
        icon: "success",
        title: "Mapeo guardado",
        text: "Las próximas sincronizaciones usarán esta correspondencia de etapas.",
        confirmButtonColor: brand.primary,
      });
      await load();
    });

  const dealStages = useMemo(
    () => pipelines.find((p) => p.id === dealPipelineDraft)?.stages ?? [],
    [pipelines, dealPipelineDraft],
  );
  const ticketStages = useMemo(
    () => ticketPipelines.find((p) => p.id === ticketPipelineDraft)?.stages ?? [],
    [ticketPipelines, ticketPipelineDraft],
  );

  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className={`flex h-full items-center justify-center text-sm ${tc.textSecondary}`}>
        <i className="ri-loader-4-line mr-2 animate-spin" />
        Cargando integración con HubSpot…
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <i className="ri-error-warning-line text-3xl text-red-500" />
        <p className="text-sm text-red-500">{error}</p>
        <button onClick={load} className="rounded-lg bg-primary-500 px-4 py-2 text-sm text-white hover:bg-primary-400">
          Reintentar
        </button>
      </div>
    );
  }

  if (!status) return null;

  const inputCls = `rounded-lg border px-2 py-1.5 text-xs outline-none ${
    isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
  }`;
  // Wider variant for the credentials form.
  // Secondary (outlined) buttons. They used to carry only a border colour, so the
  // label inherited whatever was behind it and could not be read on either theme.
  const btnSecondary = isDarkMode
    ? "border-gray-500 bg-dark-card font-medium text-white shadow-sm hover:bg-white/10"
    : "border-gray-300 bg-white font-medium text-gray-900 shadow-sm hover:bg-gray-100";

  const fieldCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${
    isDarkMode
      ? "border-dark-border bg-[#232323] text-white focus:border-primary-500"
      : "border-gray-300 bg-white text-gray-900 focus:border-primary-500"
  }`;

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-exchange-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>HubSpot</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Sincronización bidireccional del suite completo con tu CRM
              </p>
            </div>
          </div>
          <button
            onClick={load}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${btnSecondary}`}
          >
            <i className="ri-refresh-line" />
            Actualizar
          </button>
        </div>

        {/* Connection banner */}
        <div
          className={`mb-6 flex flex-wrap items-center gap-3 rounded-xl border p-4 ${
            status.connected
              ? "border-emerald-500/40 bg-emerald-500/10"
              : "border-amber-500/40 bg-amber-500/10"
          }`}
        >
          <i
            className={
              status.connected
                ? "ri-plug-fill text-2xl text-emerald-500"
                : "ri-plug-line text-2xl text-amber-500"
            }
          />
          <div className="flex-1">
            <p className={`text-sm font-semibold ${tc.text}`}>
              {status.connected ? "HubSpot conectado" : "HubSpot sin conectar"}
              {status.hubDomain ? ` · ${status.hubDomain}` : ""}
            </p>
            <p className={`text-xs ${tc.textSecondary}`}>
              {status.connected
                ? `Modo ${status.authMode === "private_app" ? "Private App" : "OAuth"}${
                    status.portalId ? ` · Portal ${status.portalId}` : ""
                  } · Job programado: ${status.schedule}`
                : "Pega abajo el token de acceso de tu app de HubSpot, o conéctate por OAuth, para empezar."}
            </p>
          </div>
          <button
            onClick={togglePause}
            disabled={busy === "pause"}
            title={status.settings.syncEnabled ? "Pausar la sincronización" : "Reanudar la sincronización"}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${
              status.settings.syncEnabled
                ? `${btnSecondary}`
                : "border-red-500/40 text-red-500 hover:bg-red-500/10"
            } disabled:opacity-50`}
          >
            <i
              className={
                busy === "pause"
                  ? "ri-loader-4-line animate-spin"
                  : status.settings.syncEnabled
                    ? "ri-pause-circle-line"
                    : "ri-play-circle-line"
              }
            />
            {status.settings.syncEnabled ? "Pausar sincronización" : "Sincronización pausada"}
          </button>
        </div>

        {/* Tabs */}
        <div className={`mb-4 flex gap-1 border-b ${tc.border}`}>
          {([
            ["conexion", "Conexión", "ri-plug-line"],
            ["objetos", "Objetos", "ri-database-2-line"],
            ["mapeo", "Mapeo de etapas", "ri-node-tree"],
            ["historial", "Historial", "ri-history-line"],
          ] as [Tab, string, string][]).map(([id, label, icon]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                tab === id
                  ? "border-b-2 border-primary-500 font-medium text-primary-500"
                  : `${tc.textSecondary} hover:text-primary-500`
              }`}
            >
              <i className={icon} />
              {label}
            </button>
          ))}
        </div>

        {/* ---------------------------------------------------------------- */}
        {tab === "conexion" && (
          <div className="space-y-5">
            {/* The tenant's own keys — this is the whole setup */}
            <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <p className={`text-sm font-semibold ${tc.text}`}>Tus llaves de HubSpot</p>
              <p className={`mb-4 text-xs ${tc.textSecondary}`}>
                Pega aquí las llaves de <b>tu propia cuenta</b> de HubSpot y quedas conectado — no hace falta
                configurar nada en el servidor ni volver a desplegar. Los secretos se guardan cifrados y nunca
                se muestran completos.
              </p>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <KeyField
                  label="Token de acceso estático"
                  hint="La ruta más simple: una sola llave. Clave de servicio (Settings → Integrations → Service keys), app con auth `static` (hs project) o app privada clásica → Auth."
                  placeholder="pat-na1-…"
                  secret
                  stored={status.credentials.accessTokenSet}
                  preview={status.credentials.accessTokenPreview}
                  value={keys.accessToken ?? ""}
                  onChange={(v) => setKeys((k) => ({ ...k, accessToken: v }))}
                  tc={tc}
                  inputCls={fieldCls}
                />
                <KeyField
                  label="Client Secret"
                  hint="Verifica la firma de los webhooks entrantes; sin él se rechazan todos. Con una clave de servicio déjalo vacío: no tiene webhooks y los cambios de HubSpot llegan cada 15 minutos."
                  placeholder="secreto de la app OAuth"
                  secret
                  stored={status.credentials.clientSecretSet}
                  preview={status.credentials.clientSecretPreview}
                  value={keys.clientSecret ?? ""}
                  onChange={(v) => setKeys((k) => ({ ...k, clientSecret: v }))}
                  tc={tc}
                  inputCls={fieldCls}
                />
                <KeyField
                  label="Client ID (OAuth)"
                  hint="Sólo para apps con auth `oauth` (varias cuentas). Lo obtienes con `hs project open` → Project Components → Auth."
                  placeholder="opcional"
                  stored={status.credentials.clientIdSet}
                  value={keys.clientId ?? ""}
                  onChange={(v) => setKeys((k) => ({ ...k, clientId: v }))}
                  tc={tc}
                  inputCls={fieldCls}
                />
                <KeyField
                  label="App ID"
                  hint="Identifica tu app; útil para rastrear los eventos que llegan al webhook."
                  placeholder="opcional"
                  stored={status.credentials.appIdSet}
                  value={keys.appId ?? ""}
                  onChange={(v) => setKeys((k) => ({ ...k, appId: v }))}
                  tc={tc}
                  inputCls={fieldCls}
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={handleSaveKeys}
                  disabled={busy === "keys"}
                  className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-50"
                >
                  <i className={busy === "keys" ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
                  Guardar y conectar
                </button>
                <a
                  href="https://developers.hubspot.com/docs/apps/developer-platform/build-apps/create-an-app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${btnSecondary}`}
                >
                  <i className="ri-external-link-line" />
                  Cómo crear la app en HubSpot
                </a>
                {(status.credentials.accessTokenSet || status.credentials.clientSecretSet) && (
                  <button
                    onClick={handleForgetKeys}
                    disabled={busy === "forget"}
                    className="flex items-center gap-2 rounded-lg border border-red-500/40 px-4 py-2 text-sm text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                  >
                    <i className="ri-delete-bin-line" />
                    Borrar llaves
                  </button>
                )}
              </div>
            </div>

            {/* Scopes to tick in HubSpot — next to the keys, because this is what
                the person creating the service key / app needs first. */}
            <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <p className={`text-sm font-semibold ${tc.text}`}>
                  Permisos (scopes) que debes marcar en HubSpot
                </p>
                <button
                  onClick={() => copy(scopesToCopy(status))}
                  className="rounded-lg border px-3 py-1 text-xs hover:bg-primary-500/10"
                >
                  <i className="ri-file-copy-line mr-1" />
                  Copiar lista
                </button>
              </div>
              <p className={`mb-3 text-xs ${tc.textSecondary}`}>
                Márcalos al crear la clave de servicio o la app en HubSpot. En verde, los que la
                conexión actual ya tiene concedidos.
              </p>
              <ul className="space-y-1.5" data-testid="hubspot-scope-guide">
                {scopeGuideOf(status).map((item) => {
                  const granted = [item.scope, ...(item.alternatives ?? [])].some((s) =>
                    status.scopes.includes(s),
                  );
                  return (
                    <li key={item.scope} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
                      <i
                        className={
                          granted
                            ? "ri-checkbox-circle-fill text-emerald-500"
                            : "ri-checkbox-blank-circle-line text-gray-400"
                        }
                      />
                      <code className={`font-mono text-[12px] ${tc.text}`}>{item.scope}</code>
                      {item.alternatives?.length ? (
                        <span className={tc.textSecondary}>
                          o{" "}
                          {item.alternatives.map((alt, i) => (
                            <span key={alt}>
                              {i > 0 ? " + " : ""}
                              <code className="font-mono text-[12px]">{alt}</code>
                            </span>
                          ))}
                        </span>
                      ) : null}
                      {item.optional && (
                        <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                          opcional
                        </span>
                      )}
                      <span className={`basis-full pl-5 ${tc.textSecondary}`}>{item.purpose}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Setup values to paste into HubSpot */}
            <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <p className={`mb-3 text-sm font-semibold ${tc.text}`}>Valores para configurar en HubSpot</p>

              <CopyRow
                label="URL del webhook (HubSpot → tu CRM)"
                value={status.webhookUrl || "(configura CRM_PUBLIC_API_BASE)"}
                onCopy={copy}
                tc={tc}
              />
              <CopyRow
                label="URL de redirección OAuth"
                value={status.redirectUri}
                onCopy={copy}
                tc={tc}
              />

              {/* Subscriptions. On HubSpot's current developer platform these
                  live in the project's config file, so hand over the file
                  itself; the chip list below covers the legacy settings screen. */}
              <div className="mt-4">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <p className={`text-xs font-medium ${tc.textSecondary}`}>
                    Suscripciones de webhook — <code>webhooks-hsmeta.json</code>
                  </p>
                  <button
                    onClick={() => copy(JSON.stringify(status.webhooksConfig, null, 2))}
                    className="rounded-lg border px-3 py-1 text-xs hover:bg-primary-500/10"
                  >
                    <i className="ri-file-copy-line mr-1" />
                    Copiar archivo
                  </button>
                </div>
                <p className={`mb-2 text-xs ${tc.textSecondary}`}>
                  En la plataforma de desarrollo actual de HubSpot las suscripciones se declaran en un
                  archivo del proyecto (<code>src/app/webhooks/webhooks-hsmeta.json</code>), no en una
                  pantalla. Éste ya apunta a tu despliegue: pégalo tal cual y ejecuta{" "}
                  <code>hs project upload</code>.
                </p>
                <pre
                  className={`max-h-64 overflow-auto rounded-lg bg-black/5 p-3 text-[11px] leading-relaxed dark:bg-white/10 ${tc.text}`}
                >
                  {JSON.stringify(status.webhooksConfig, null, 2)}
                </pre>
                <p className={`mt-2 text-xs ${tc.textSecondary}`}>
                  ¿Usas una app privada clásica (Configuración → Integraciones → Private Apps)? Ahí las
                  suscripciones se marcan a mano, una por una:
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {status.recommendedSubscriptions.map((s) => (
                    <span
                      key={s}
                      className="rounded-full bg-primary-500/10 px-2 py-0.5 text-[11px] text-primary-500"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              {/* URL overrides — only needed behind a custom domain or proxy */}
              <button
                onClick={() => setAdvancedOpen((v) => !v)}
                className={`mt-4 flex items-center gap-1 text-xs ${tc.textSecondary} hover:text-primary-500`}
              >
                <i className={advancedOpen ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} />
                Ajustes avanzados de URL (dominio propio o proxy)
              </button>
              {advancedOpen && (
                <div className="mt-3 space-y-3">
                  <p className={`text-xs ${tc.textSecondary}`}>
                    Normalmente no hace falta tocar nada: las dos URL de arriba se derivan solas. Rellena estos
                    campos sólo si tu API vive detrás de un dominio propio y HubSpot debe llamar a otra dirección.
                  </p>
                  <LabeledInput
                    label="Base pública de la API"
                    placeholder={status.publicApiBase || "https://api.tudominio.com"}
                    value={apiBaseDraft}
                    onChange={setApiBaseDraft}
                    tc={tc}
                    inputCls={fieldCls}
                  />
                  <LabeledInput
                    label="URL del webhook (sobrescribe)"
                    placeholder={status.webhookUrl || ""}
                    value={webhookDraft}
                    onChange={setWebhookDraft}
                    tc={tc}
                    inputCls={fieldCls}
                  />
                  <LabeledInput
                    label="URL de redirección OAuth (sobrescribe)"
                    placeholder={status.redirectUri || ""}
                    value={redirectDraft}
                    onChange={setRedirectDraft}
                    tc={tc}
                    inputCls={fieldCls}
                  />
                  <button
                    onClick={handleSaveUrls}
                    disabled={busy === "urls"}
                    className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${btnSecondary} disabled:opacity-50`}
                  >
                    <i className={busy === "urls" ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
                    Guardar URLs
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {status.oauthConfigured && (
                <button
                  onClick={handleConnect}
                  disabled={busy === "connect"}
                  className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-50"
                >
                  <i className={busy === "connect" ? "ri-loader-4-line animate-spin" : "ri-links-line"} />
                  Conectar con HubSpot
                </button>
              )}
              <button
                onClick={handleTest}
                disabled={busy === "test" || !status.connected}
                className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${btnSecondary} disabled:opacity-50`}
              >
                <i className={busy === "test" ? "ri-loader-4-line animate-spin" : "ri-pulse-line"} />
                Probar conexión
              </button>
              <button
                onClick={handleOwners}
                disabled={busy === "owners" || !status.connected}
                className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${btnSecondary} disabled:opacity-50`}
              >
                <i className={busy === "owners" ? "ri-loader-4-line animate-spin" : "ri-team-line"} />
                Sincronizar propietarios
              </button>
              {status.authMode === "oauth" && (
                <button
                  onClick={handleDisconnect}
                  disabled={busy === "disconnect"}
                  className="flex items-center gap-2 rounded-lg border border-red-500/40 px-4 py-2 text-sm text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                >
                  <i className="ri-plug-line" />
                  Desconectar
                </button>
              )}
            </div>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {tab === "objetos" && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => runSync()}
                disabled={!!busy || !status.connected}
                className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-50"
              >
                <i className={busy === "sync:all" ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} />
                Sincronizar ahora
              </button>
              <button
                onClick={handleFullImport}
                disabled={!!busy || !status.connected}
                className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${btnSecondary} disabled:opacity-50`}
              >
                <i className={busy === "sync:full" ? "ri-loader-4-line animate-spin" : "ri-download-cloud-2-line"} />
                {importStep ? `Importando ${importStep}…` : "Importación completa"}
              </button>
              <button
                onClick={handleMarketing}
                disabled={!!busy || !status.connected}
                className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${btnSecondary} disabled:opacity-50`}
              >
                <i className={busy === "marketing" ? "ri-loader-4-line animate-spin" : "ri-megaphone-line"} />
                Marketing (formularios, campañas, listas)
              </button>
              <button
                onClick={handleAttachments}
                disabled={!!busy || !status.connected}
                title="Copia al CRM los archivos adjuntos a notas, correos, llamadas, reuniones y tareas"
                className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${btnSecondary} disabled:opacity-50`}
              >
                <i className={busy === "attachments" ? "ri-loader-4-line animate-spin" : "ri-attachment-2"} />
                Importar adjuntos
              </button>
            </div>

            {/* Product stamped on what is imported. HubSpot has no such field, and a
                portal usually belongs to one product line. */}
            <div className={`flex flex-wrap items-center gap-3 rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-semibold ${tc.text}`}>Producto de lo importado</p>
                <p className={`text-xs ${tc.textSecondary}`}>
                  Se asigna a los contactos, empresas y negocios que lleguen de HubSpot sin producto.
                  No cambia los que ya tienen uno. Para aplicarlo a lo ya importado, vuelve a lanzar
                  «Importación completa».
                </p>
              </div>
              <select
                aria-label="Producto de lo importado"
                value={status.settings.defaultProduct ?? ""}
                disabled={!!busy}
                onChange={(e) => changeDefaultProduct(e.target.value)}
                className={`${fieldCls} max-w-[220px]`}
              >
                <option value="">Sin producto</option>
                {LEAD_PRODUCTS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            {/* One HubSpot pipeline per product line: the pipeline of a deal says
                which product it is, and its contact and company follow it. */}
            <div data-testid="hubspot-product-by-pipeline" className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold ${tc.text}`}>Producto por pipeline de HubSpot</p>
                  <p className={`text-xs ${tc.textSecondary}`}>
                    Los negocios de cada pipeline toman este producto, y con ellos su contacto, su empresa y su
                    cotización. Gana sobre «Producto de lo importado». No cambia un producto elegido a mano en el CRM.
                  </p>
                </div>
                <button
                  onClick={handleApplyProducts}
                  disabled={!!busy || !Object.keys(status.settings.productByPipeline ?? {}).length}
                  title="Corrige el producto de lo que ya está importado, sin volver a importar de HubSpot"
                  className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${btnSecondary} disabled:opacity-50`}
                >
                  <i className={busy === "applyProducts" ? "ri-loader-4-line animate-spin" : "ri-price-tag-3-line"} />
                  Aplicar a lo ya importado
                </button>
              </div>
              {pipelines.length === 0 ? (
                <p className={`mt-3 text-xs ${tc.textSecondary}`}>
                  {status.connected ? "Cargando los pipelines de HubSpot…" : "Conecta HubSpot para ver sus pipelines."}
                </p>
              ) : (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {pipelines.map((p) => (
                    <label key={p.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className={`min-w-0 truncate ${tc.text}`} title={p.label}>
                        {p.label}
                      </span>
                      <select
                        aria-label={`Producto del pipeline ${p.label}`}
                        value={status.settings.productByPipeline?.[p.id] ?? ""}
                        disabled={!!busy}
                        onChange={(e) => changePipelineProduct(p.id, e.target.value)}
                        className={`${fieldCls} max-w-[200px]`}
                      >
                        <option value="">El de «Producto de lo importado»</option>
                        {LEAD_PRODUCTS.map((product) => (
                          <option key={product} value={product}>
                            {product}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className={`overflow-x-auto rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <table className="w-full text-xs">
                <thead className="bg-primary-500 text-white">
                  <tr>
                    <th className="rounded-tl-lg px-3 py-2 text-left">Objeto</th>
                    <th className="px-3 py-2 text-left">HubSpot</th>
                    <th className="px-3 py-2 text-left">Colección</th>
                    <th className="px-3 py-2 text-center">Vinculados</th>
                    <th className="px-3 py-2 text-center">Dirección</th>
                    <th className="px-3 py-2 text-center">Activo</th>
                    <th className="rounded-tr-lg px-3 py-2 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {status.objects.map((obj) => (
                    <tr key={obj.key} className={`border-b last:border-0 ${tc.border}`}>
                      <td className={`px-3 py-2 font-medium ${tc.text}`}>{obj.label}</td>
                      <td className={`px-3 py-2 ${tc.textSecondary}`}>
                        <code>{obj.hubspotType}</code>
                      </td>
                      <td className={`px-3 py-2 ${tc.textSecondary}`}>
                        <code>{obj.collection}</code>
                        {obj.alsoPush.map((c) => (
                          <code key={c} className="ml-1 opacity-70">
                            +{c}
                          </code>
                        ))}
                      </td>
                      <td className={`px-3 py-2 text-center ${tc.text}`}>{obj.linked}</td>
                      <td className="px-3 py-2 text-center">
                        {obj.key === "owners" ? (
                          <span className={tc.textSecondary}>Solo importar</span>
                        ) : (
                          <select
                            value={obj.direction}
                            onChange={(e) => changeDirection(obj, e.target.value as SyncDirection)}
                            disabled={busy === `dir:${obj.key}`}
                            className={inputCls}
                          >
                            {status.directions.map((d) => (
                              <option key={d} value={d}>
                                {DIRECTION_LABELS[d]}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {obj.key === "owners" ? (
                          <i className="ri-checkbox-circle-fill text-emerald-500" />
                        ) : (
                          <button
                            onClick={() => toggleObject(obj)}
                            disabled={busy === `toggle:${obj.key}`}
                            title={obj.enabled ? "Desactivar" : "Activar"}
                            className={obj.enabled ? "text-emerald-500" : "text-gray-400"}
                          >
                            <i className={obj.enabled ? "ri-toggle-fill text-xl" : "ri-toggle-line text-xl"} />
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => (obj.key === HUBSPOT_OWNERS_KEY ? handleOwners() : runSync([obj.key]))}
                          disabled={!!busy || !status.connected}
                          className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10 disabled:opacity-40"
                          title="Sincronizar sólo este objeto"
                        >
                          <i
                            className={
                              busy === `sync:${obj.key}` ? "ri-loader-4-line animate-spin" : "ri-refresh-line"
                            }
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className={`text-xs ${tc.textSecondary}`}>
              <i className="ri-information-line mr-1" />
              Colecciones creadas por esta integración:{" "}
              {status.newCollections.map((c) => (
                <code key={c} className="mr-1">
                  {c}
                </code>
              ))}
            </p>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {tab === "mapeo" && (
          <div className="space-y-5">
            {!status.connected ? (
              <p className={`text-sm ${tc.textSecondary}`}>
                Conecta HubSpot para cargar sus pipelines y etapas.
              </p>
            ) : (
              <>
                <StageMapCard
                  title="Negocios — pipeline y etapas"
                  hint="Cada etapa del tablero del CRM se envía a HubSpot como la etapa que elijas aquí (y al revés al importar)."
                  pipelines={pipelines}
                  pipelineId={dealPipelineDraft}
                  onPipelineChange={setDealPipelineDraft}
                  localStages={LOCAL_DEAL_STAGES}
                  stages={dealStages}
                  draft={dealStageDraft}
                  onChange={(local, value) => setDealStageDraft((d) => ({ ...d, [local]: value }))}
                  tc={tc}
                  inputCls={inputCls}
                />
                <StageMapCard
                  title="Tickets — pipeline y estados"
                  hint="Aplica a la colección crm_tickets, creada por esta integración."
                  pipelines={ticketPipelines}
                  pipelineId={ticketPipelineDraft}
                  onPipelineChange={setTicketPipelineDraft}
                  localStages={LOCAL_TICKET_STATUSES}
                  stages={ticketStages}
                  draft={ticketStageDraft}
                  onChange={(local, value) => setTicketStageDraft((d) => ({ ...d, [local]: value }))}
                  tc={tc}
                  inputCls={inputCls}
                />
                <button
                  onClick={saveMaps}
                  disabled={busy === "maps"}
                  className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-50"
                >
                  <i className={busy === "maps" ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
                  Guardar mapeo
                </button>
              </>
            )}
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {tab === "historial" && (
          <div className="space-y-5">
            <div className={`overflow-x-auto rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <p className={`mb-3 text-sm font-semibold ${tc.text}`}>Corridas de sincronización</p>
              <table className="w-full text-xs">
                <thead className="bg-primary-500 text-white">
                  <tr>
                    <th className="rounded-tl-lg px-3 py-2 text-left">Inicio</th>
                    <th className="px-3 py-2 text-left">Modo</th>
                    <th className="px-3 py-2 text-center">Recibidos</th>
                    <th className="px-3 py-2 text-center">Enviados</th>
                    <th className="px-3 py-2 text-center">Creados</th>
                    <th className="px-3 py-2 text-center">Duración</th>
                    <th className="rounded-tr-lg px-3 py-2 text-center">Errores</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 && (
                    <tr>
                      <td colSpan={7} className={`px-3 py-6 text-center ${tc.textSecondary}`}>
                        Aún no hay corridas registradas.
                      </td>
                    </tr>
                  )}
                  {runs.map((r) => (
                    <tr key={r.id} className={`border-b last:border-0 ${tc.border}`}>
                      <td className={`px-3 py-2 ${tc.text}`}>{fmtDate(r.startedAt)}</td>
                      <td className={`px-3 py-2 ${tc.textSecondary}`}>
                        {r.mode === "full" ? "Completa" : "Incremental"}
                      </td>
                      <td className={`px-3 py-2 text-center ${tc.text}`}>{r.totals?.pulled ?? 0}</td>
                      <td className={`px-3 py-2 text-center ${tc.text}`}>{r.totals?.pushed ?? 0}</td>
                      <td className={`px-3 py-2 text-center ${tc.text}`}>{r.totals?.created ?? 0}</td>
                      <td className={`px-3 py-2 text-center ${tc.textSecondary}`}>
                        {r.durationMs ? `${Math.round(r.durationMs / 1000)}s` : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {r.totals?.errors ? (
                          <span title={(r.errors || []).join("\n")} className="text-red-500">
                            {r.totals.errors}
                          </span>
                        ) : (
                          <span className="text-emerald-500">0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={`overflow-x-auto rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <p className={`mb-3 text-sm font-semibold ${tc.text}`}>Eventos recibidos de HubSpot (webhook)</p>
              <table className="w-full text-xs">
                <thead className="bg-primary-500 text-white">
                  <tr>
                    <th className="rounded-tl-lg px-3 py-2 text-left">Cuándo</th>
                    <th className="px-3 py-2 text-left">Evento</th>
                    <th className="px-3 py-2 text-left">Registro</th>
                    <th className="px-3 py-2 text-left">Propiedad</th>
                    <th className="rounded-tr-lg px-3 py-2 text-left">Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 && (
                    <tr>
                      <td colSpan={5} className={`px-3 py-6 text-center ${tc.textSecondary}`}>
                        Sin eventos todavía. Registra la URL del webhook en HubSpot para recibirlos.
                      </td>
                    </tr>
                  )}
                  {events.map((e) => (
                    <tr key={e.id} className={`border-b last:border-0 ${tc.border}`}>
                      <td className={`px-3 py-2 ${tc.text}`}>{fmtDate(e.occurredAt)}</td>
                      <td className={`px-3 py-2 ${tc.textSecondary}`}>
                        <code>{e.subscriptionType}</code>
                      </td>
                      <td className={`px-3 py-2 ${tc.textSecondary}`}>
                        {e.objectKey} · {e.hubspotId}
                      </td>
                      <td className={`px-3 py-2 ${tc.textSecondary}`}>{e.propertyName || "—"}</td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            e.status === "failed"
                              ? "rounded-full bg-red-500/15 px-2 py-0.5 text-red-500"
                              : "rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-500"
                          }
                          title={e.error || ""}
                        >
                          {e.outcome || e.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

/**
 * One credential input. A stored secret is never sent back by the API, so the
 * field shows a masked preview and stays EMPTY — typing replaces it, leaving it
 * blank keeps what is stored.
 */
function KeyField({
  label,
  hint,
  placeholder,
  secret = false,
  stored,
  preview,
  value,
  onChange,
  tc,
  inputCls,
}: {
  label: string;
  hint: string;
  placeholder: string;
  secret?: boolean;
  stored: boolean;
  preview?: string | null;
  value: string;
  onChange: (v: string) => void;
  tc: ReturnType<typeof buildThemeClasses>;
  inputCls: string;
}) {
  const [reveal, setReveal] = useState(false);
  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <label className={`text-sm font-medium ${tc.text}`}>{label}</label>
        {stored ? (
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] text-emerald-500">
            {preview || "guardado"}
          </span>
        ) : (
          <span className="rounded-full bg-gray-400/15 px-2 py-0.5 text-[11px] text-gray-500">sin configurar</span>
        )}
      </div>
      <div className="relative">
        <input
          type={secret && !reveal ? "password" : "text"}
          value={value}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
          placeholder={stored && secret ? "Escribe para reemplazar" : placeholder}
          className={`${inputCls} ${secret ? "pr-10" : ""}`}
        />
        {secret && (
          <button
            type="button"
            onClick={() => setReveal((v) => !v)}
            title={reveal ? "Ocultar" : "Mostrar"}
            className={`absolute right-2 top-1/2 -translate-y-1/2 ${tc.textSecondary} hover:text-primary-500`}
          >
            <i className={reveal ? "ri-eye-off-line" : "ri-eye-line"} />
          </button>
        )}
      </div>
      <p className={`mt-1 text-xs ${tc.textSecondary}`}>{hint}</p>
    </div>
  );
}

/** Plain labelled text input, for the advanced URL overrides. */
function LabeledInput({
  label,
  placeholder,
  value,
  onChange,
  tc,
  inputCls,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  tc: ReturnType<typeof buildThemeClasses>;
  inputCls: string;
}) {
  return (
    <div>
      <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
      />
    </div>
  );
}

function CopyRow({
  label,
  value,
  onCopy,
  tc,
}: {
  label: string;
  value: string;
  onCopy: (v: string) => void;
  tc: ReturnType<typeof buildThemeClasses>;
}) {
  return (
    <div className="mb-3">
      <p className={`mb-1 text-xs font-medium ${tc.textSecondary}`}>{label}</p>
      <div className="flex items-center gap-2">
        <code className={`flex-1 truncate rounded-lg bg-black/5 px-2 py-1.5 text-xs dark:bg-white/10 ${tc.text}`}>
          {value}
        </code>
        <button
          onClick={() => onCopy(value)}
          className="rounded-lg border px-3 py-1.5 text-xs hover:bg-primary-500/10"
          title="Copiar"
        >
          <i className="ri-file-copy-line" />
        </button>
      </div>
    </div>
  );
}

function StageMapCard({
  title,
  hint,
  pipelines,
  pipelineId,
  onPipelineChange,
  localStages,
  stages,
  draft,
  onChange,
  tc,
  inputCls,
}: {
  title: string;
  hint: string;
  pipelines: HubspotPipeline[];
  pipelineId: string;
  onPipelineChange: (id: string) => void;
  localStages: string[];
  stages: HubspotPipeline["stages"];
  draft: Record<string, string>;
  onChange: (local: string, value: string) => void;
  tc: ReturnType<typeof buildThemeClasses>;
  inputCls: string;
}) {
  return (
    <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
      <p className={`text-sm font-semibold ${tc.text}`}>{title}</p>
      <p className={`mb-3 text-xs ${tc.textSecondary}`}>{hint}</p>

      <div className="mb-4 flex items-center gap-2">
        <span className={`text-xs ${tc.textSecondary}`}>Pipeline de HubSpot</span>
        <select value={pipelineId} onChange={(e) => onPipelineChange(e.target.value)} className={inputCls}>
          {pipelines.length === 0 && <option value={pipelineId}>{pipelineId}</option>}
          {pipelines.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {localStages.map((local) => (
          <div key={local} className="flex items-center gap-2">
            <span className={`w-32 shrink-0 text-xs capitalize ${tc.text}`}>{local.replace(/_/g, " ")}</span>
            <i className={`ri-arrow-right-line text-xs ${tc.textSecondary}`} />
            <select
              value={draft[local] || ""}
              onChange={(e) => onChange(local, e.target.value)}
              className={`${inputCls} flex-1`}
            >
              <option value="">— sin mapear —</option>
              {stages.length === 0 && draft[local] && <option value={draft[local]}>{draft[local]}</option>}
              {stages.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
